import { useSecureContext } from "../../../hooks/secure-http-context";
import React, { useEffect, useState } from "react";
import { LinkComponent } from "../../shared/LinkComponent";
import { RouteConfig } from "../../../routes/AuthRoutes";
import { NblocksButton } from "../../shared/NblocksButton";
import { InputComponent } from "../../shared/InputComponent";
import { TextComponent } from "../../shared/TextComponent";
import { AlertComponent } from "../../shared/AlertComponent";
import { UnauthenticatedError } from "../../../utils/errors/UnauthenticatedError";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { startAuthentication } from "@simplewebauthn/browser";
import { LoginAlternativesComponent } from "./LoginAlternativesComponent";
import { FederationConnectionsComponent } from "./FederationConnectionsComponent";
import { useApp } from "../../../hooks/app-context";
import { useNavigate } from "react-router-dom";
import { Transition } from "@headlessui/react";
const LoginComponent = ({
  initalError,
  errorDetails,
  didLogin,
  didClickFederatedLogin,
  didClickFederationConnection
}) => {
  const {
    authService
  } = useSecureContext();
  const [username, setUsername] = useState("");
  const [credentialsInputMode, setCredentialsInputMode] = useState("USERNAME");
  const [federationConnections, setFederationConnections] = useState([]);
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isloading, setIsLoading] = useState(false);
  const {
    tenantSelfSignup
  } = useApp();
  const {
    t
  } = useTranslation();
  // We're using states to pass between views, therefore useNavigate()
  const navigate = useNavigate();
  const wrongCredentialsErrorMsg = t("Wrong credentials, please try again.");
  const generalErrorMsg = t("There was an error when logging in. Try again, otherwise contact support.");
  const initialErrorMsg = t("There was an error when logging in. Are you sure you already have signed up for an account?");
  const magicLinkExpiredErrorMsg = t("The magic link has expired, or you're not using the same browser that you sent the link from.");
  useEffect(() => {
    if (initalError) {
      if (errorDetails === "mle") setErrorMsg(magicLinkExpiredErrorMsg);else setErrorMsg(initialErrorMsg);
    }
  }, [initalError]);

  /**
   * Password manager plugins like 1Password can interfare with native browser webauthn autofill
   * @param autofill
   */
  const onDidPasskeysAuthenticate = async autofill => {
    if (!autofill) {
      setIsLoading(true);
    }
    try {
      const passkeysAuthOptions = await authService.passkeysAuthenticationOptions();
      if (passkeysAuthOptions) {
        const authResult = await startAuthentication(passkeysAuthOptions, autofill);
        setIsLoading(true);
        const {
          mfaState,
          tenantUserId
        } = await authService.passkeysAuthenticate(authResult);
        didLogin(mfaState, tenantUserId);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  const submit = async event => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMsg("");
    try {
      switch (credentialsInputMode) {
        case "USERNAME":
          await checkCredentialsConfig();
          break;
        case "PASSWORD":
          await loginWithPassword();
          break;
        case "RESET-PASSWORD":
          navigate(RouteConfig.password.resetPasswordScreen, {
            state: {
              username
            }
          });
          break;
      }
    } catch (error) {
      if (error instanceof UnauthenticatedError) {
        setErrorMsg(wrongCredentialsErrorMsg);
      } else {
        setErrorMsg(generalErrorMsg);
      }
      setPassword("");
    } finally {
      setIsLoading(false);
    }
  };
  const loginWithPassword = async () => {
    const {
      mfaState,
      tenantUserId
    } = await authService.authenticate(username, password);
    didLogin(mfaState, tenantUserId);
  };
  const checkCredentialsConfig = async () => {
    const {
      hasPassword,
      federationConnections
    } = await authService.getCredentialsConfig(username);
    if (federationConnections.length > 0) {
      setFederationConnections(federationConnections);
      setCredentialsInputMode("FEDERATION-CONNECTIONS");
    } else {
      if (hasPassword) {
        setCredentialsInputMode("PASSWORD");
      } else {
        setCredentialsInputMode("RESET-PASSWORD");
      }
    }
  };
  const resetFlow = () => {
    setCredentialsInputMode("USERNAME");
    setUsername("");
    setPassword("");
    setErrorMsg("");
    setIsLoading(false);
  };
  const ssoLoginMiddleware = type => {
    didClickFederatedLogin(type);
  };
  const didClickMagicLink = () => {
    navigate(RouteConfig.login.magicLinkScreen, {
      state: {
        username
      }
    });
  };
  const renderCredentialsInput = () => {
    switch (credentialsInputMode) {
      case "USERNAME":
        return /*#__PURE__*/React.createElement(InputComponent, {
          type: "email",
          label: t("Email address"),
          placeholder: t("Enter your email"),
          name: "username",
          onChange: event => setUsername(event.target.value),
          value: username,
          autoComplete: "username webauthn",
          autoFocus: true
        });
      case "PASSWORD":
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InputComponent, {
          type: "password",
          label: t("Password"),
          placeholder: t("Enter your password"),
          name: "password",
          onChange: event => setPassword(event.target.value),
          value: password
        }), /*#__PURE__*/React.createElement("div", {
          className: "flex justify-end"
        }, /*#__PURE__*/React.createElement(LinkComponent, {
          to: RouteConfig.password.resetPasswordScreen,
          handoverProps: {
            username
          },
          type: "primary",
          size: "sm",
          className: "font-semibold"
        }, t("Forgot password"))));
      case "RESET-PASSWORD":
        return /*#__PURE__*/React.createElement(TextComponent, null, "You need to reset your password");
    }
  };
  const getSubmitButtonText = () => {
    switch (credentialsInputMode) {
      case "USERNAME":
        return t("Continue");
      case "PASSWORD":
        return t("Sign in");
      case "RESET-PASSWORD":
        return t("Set password");
    }
  };
  const getSubmitButtonDisabled = () => {
    switch (credentialsInputMode) {
      case "USERNAME":
        return !username;
      case "PASSWORD":
        return !username;
      case "RESET-PASSWORD":
        return false;
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "max-w-sm w-full mb-6"
  }, /*#__PURE__*/React.createElement(Transition, {
    show: !!errorMsg,
    enter: "transition duration-100 ease-out",
    enterFrom: "transform scale-95 opacity-0",
    enterTo: "transform scale-100 opacity-100",
    leave: "transition duration-75 ease-out",
    leaveFrom: "transform scale-100 opacity-100",
    leaveTo: "transform scale-95 opacity-0"
  }, /*#__PURE__*/React.createElement(AlertComponent, {
    type: "danger",
    title: t("Could not login"),
    messages: [errorMsg]
  }))), /*#__PURE__*/React.createElement("form", {
    onSubmit: event => submit(event),
    className: "space-y-6 max-w-sm w-full"
  }, renderCredentialsInput(), credentialsInputMode !== "FEDERATION-CONNECTIONS" && /*#__PURE__*/React.createElement(NblocksButton, {
    submit: true,
    disabled: getSubmitButtonDisabled(),
    size: "md",
    type: "primary",
    isLoading: isloading,
    fullWidth: true
  }, getSubmitButtonText()), credentialsInputMode === "USERNAME" && /*#__PURE__*/React.createElement(LoginAlternativesComponent, {
    didClickMagicLinkAuthenticate: didClickMagicLink,
    didClickPasskeysAuthenticate: onDidPasskeysAuthenticate,
    didClickSocialLogin: ssoLoginMiddleware
  }), credentialsInputMode === "FEDERATION-CONNECTIONS" && /*#__PURE__*/React.createElement(FederationConnectionsComponent, {
    connections: federationConnections,
    didClickConnection: didClickFederationConnection
  })), credentialsInputMode != "USERNAME" && /*#__PURE__*/React.createElement("div", {
    className: "mt-8"
  }, /*#__PURE__*/React.createElement(NblocksButton, {
    onClick: () => resetFlow()
  }, /*#__PURE__*/React.createElement(LinkComponent, {
    to: "#",
    size: "sm",
    className: "font-semibold flex items-center",
    type: "secondary"
  }, /*#__PURE__*/React.createElement(ArrowLeftIcon, {
    className: "w-5 inline-block mr-1"
  }), " ", t("Try a different email")))), tenantSelfSignup && /*#__PURE__*/React.createElement("div", {
    className: "mt-8"
  }, /*#__PURE__*/React.createElement(TextComponent, {
    size: "sm"
  }, t("Don't have an account?"), "\xA0", /*#__PURE__*/React.createElement(LinkComponent, {
    to: RouteConfig.tenant.signupScreen,
    type: "primary",
    size: "sm",
    className: "font-semibold"
  }, t("Sign up")))));
};
export { LoginComponent };